import { useManageSageProfile } from '@rabbit/bizproc/react';
import {
  Input,
  Button,
  Modal,
  asyncValidateSchema,
} from '@rabbit/elements/shared-components';
import { DTRepairer_Public } from '@rabbit/data/types';
import { UserContext } from '@rabbit/sage/context/UserContext';
import { Formik, Form } from 'formik';
import { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

interface FormValuesShape {
  current_password: string;
  new_password: string;
  retype_password: string;
}

const initialValues: FormValuesShape = {
  current_password: '',
  new_password: '',
  retype_password: '',
};

const schema = Yup.object().shape({
  current_password: Yup.string().required('Please type your current password'),
  new_password: Yup.string()
    .min(8, 'Password should be 8 or more characters long')
    .matches(/(?=.*[a-z])/, 'Please enter at least 1 lowercase letter')
    .matches(/(?=.*[A-Z])/, 'Please enter at least 1 uppercase letter')
    .matches(/^(?=.*[0-9])/, 'Please enter at least 1 number')
    .required('Please enter a new password'),
  retype_password: Yup.string()
    .min(8, 'Password should be 8 or more characters')
    .matches(/(?=.*[a-z])/, 'Please enter at least 1 lowercase letter')
    .matches(/(?=.*[A-Z])/, 'Please enter at least 1 uppercase letter')
    .matches(/^(?=.*[0-9])/, 'Please enter at least 1 number')
    .required('Please confirm your password')
    .oneOf([Yup.ref('new_password')], 'Passwords must match'),
});

export function ChangePasswordModal({
  handleClose,
  user,
}: {
  handleClose: () => void;
  user?: DTRepairer_Public;
}) {
  const contextValues = useContext(UserContext);
  const handleSignOut = contextValues?.handleSignout;
  const { updatePassword } = useManageSageProfile();
  const { t } = useTranslation();
  const modalSettings = {
    title: t('general.changePassword'),
    handleClose: () => handleClose(),
  };

  const onSubmit = async (values: FormValuesShape) => {
    setLoading(true);
    try {
      await updatePassword(values.current_password, values.new_password);
      toast.success(t('message.passwordUpdatedSuccessfully'));
      setLoading(false);
      handleClose();
      handleSignOut && handleSignOut();
    } catch (err: any) {
      setLoading(false);
      if (err && err.message && err.message.match('wrong-password')) {
        toast.error(t('message.incorrectPasswordTryAgain'));
      } else toast.error(t('message.couldNotUpdatePassword'));
    }
  };

  const [loading, setLoading] = useState(false);

  return (
    <Modal
      kind="generic"
      settings={modalSettings}
      className="m-auto w-full max-w-[720px] rounded-md border"
      isLoading={loading}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={schema}
        validate={asyncValidateSchema(schema)}
        validateOnBlur
      >
        {({ values, errors }) => {
          return (
            <Form>
              <div className="flex flex-col gap-4 px-5 pt-4">
                <Input
                  type="password"
                  name="current_password"
                  label={`${t('message.currentPassword')}*`}
                  settings={{
                    placeholder: t('message.enterYourCurrentPassword'),
                    hint: '*required',
                  }}
                />
                <Input
                  type="password"
                  name="new_password"
                  label={`${t('message.newPassword')}*`}
                  settings={{
                    popoverTarget: 'popover-password',
                    popoverPlacement: 'bottom',
                    popoverTrigger: 'hover',
                    placeholder: t('message.enterYourNewPassword'),
                    hint: '*required',
                  }}
                />
                <Input
                  type="password"
                  name="retype_password"
                  label={`${t('message.retypePassword')}*`}
                  settings={{
                    placeholder: t('message.retypeYourNewPassword'),
                    hint: '*required',
                  }}
                />
                <div className="flex gap-4">
                  <Button
                    kind="primary"
                    children={t('general.save')}
                    type="submit"
                    onClick={() => 0}
                  />
                  <Button
                    kind="red"
                    children={t('general.cancel')}
                    type="button"
                    onClick={() => handleClose()}
                  />
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}
